// ABC-анализ меню (docs/tasks/task-menu-abc.md): чистые функции без React.
// Вход — строки OLAP /menu_abc (ресторан, группа, блюдо и его ID, категория; выручка, себестоимость, прибыль,
// порции), выбор из шапки и категория. Порядок: фильтр выбора → категория → сумма по блюду (по ID iiko, нет ID —
// по названию; возвраты гасят продажи внутри блюда) → исключение блюд с выручкой ≤ 0 или порциями ≤ 0 → буквы
// по порциям, выручке и прибыли. Ключ блюда (key) — для сравнения периодов (abcDynamics) и списков.
import { matchesSelection } from '../../../utils/restaurant';
import { olapNumber, olapText } from '../../../utils/olapFields';

export const ALL = 'Все';
export const NO_CATEGORY = 'Без категории';
// Как MENU_ABC_CATEGORY_FIELD на бэкенде («Категория блюда»)
export const ABC_CATEGORY_FIELD = 'DishCategory';

const FIELDS = {
  name: 'DishName',
  id: 'DishId', // как MENU_ABC_ID_FIELD на бэкенде
  revenue: 'DishDiscountSumInt',
  cost: 'ProductCostBase.ProductCost',
  profit: 'ProductCostBase.Profit',
  qty: 'DishAmountInt',
};

// A — пока накопленная доля ≤ 80 %, B — ≤ 95 %, иначе C. Запас — на погрешность сложения дробей
const A_SHARE = 0.8;
const B_SHARE = 0.95;
const EPSILON = 1e-9;
export const LETTERS = ['A', 'B', 'C'];

export const ABC_LETTER_COLORS = { A: '#66bb6a', B: '#f0a43a', C: '#ef6b6b' };

// Рекомендации — первое подходящее правило (tagOf); keep отдельной кнопкой не показывается
export const ABC_TAGS = {
  check: { label: 'Проверить техкарту', hint: 'Нет себестоимости — прибыль завышена' },
  core: { label: 'Основа меню', hint: 'Лидеры по продажам, выручке и прибыли' },
  promote: { label: 'Продвигать', hint: 'Прибыльные, но продаются редко' },
  remove: { label: 'Кандидат на вывод', hint: 'Мало продаж, выручки и прибыли' },
  keep: { label: 'Оставить как есть', hint: 'Без особых действий' },
};
export const TAG_ORDER = ['core', 'promote', 'remove', 'check', 'keep'];

// Ничья — по названию, у разных блюд с одним названием — по ключу (порядок всегда один)
const byName = (a, b) => a.name.localeCompare(b.name, 'ru') || String(a.key).localeCompare(String(b.key));

export const categoryOf = (row) => olapText(row, ABC_CATEGORY_FIELD).trim() || NO_CATEGORY;

// Буква каждого блюда по показателю: по убыванию (ничья — по названию), накопленная доля от итога.
// Первое блюдо — всегда A; итог ≤ 0 — все C
export function lettersBy(dishes, key) {
  const letters = new Map();
  const total = dishes.reduce((sum, d) => sum + d[key], 0);
  if (!(total > 0)) {
    dishes.forEach((d) => letters.set(d.key, 'C'));
    return letters;
  }
  let cumulative = 0;
  [...dishes]
    .sort((a, b) => b[key] - a[key] || byName(a, b))
    .forEach((dish, index) => {
      cumulative += dish[key];
      const share = cumulative / total;
      const letter = index === 0 || share <= A_SHARE + EPSILON ? 'A' : share <= B_SHARE + EPSILON ? 'B' : 'C';
      letters.set(dish.key, letter);
    });
  return letters;
}

export function tagOf(dish) {
  const { qty, revenue, profit } = dish.abc;
  if (dish.noCost) return 'check';
  if (qty === 'A' && revenue === 'A' && profit === 'A') return 'core';
  if (qty === 'C' && revenue === 'C' && profit === 'C') return 'remove';
  if (qty === 'C' && (profit === 'A' || profit === 'B')) return 'promote';
  return 'keep';
}

// Строки выбора и категории, сложенные по блюду: по ID iiko (переименованное — то же блюдо), без ID — по названию.
// Одно блюдо из нескольких ресторанов или групп — одно. Переименовали внутри периода — название с большими продажами
function sumByDish(rows) {
  const dishes = new Map();
  for (const row of rows) {
    const name = olapText(row, FIELDS.name).trim() || 'Без названия';
    const id = olapText(row, FIELDS.id).trim();
    const key = id ? `id:${id}` : `name:${name}`;
    const dish = dishes.get(key) || { key, name, names: {}, category: '', qty: 0, revenue: 0, cost: 0, profit: 0 };
    if (!dish.category && olapText(row, ABC_CATEGORY_FIELD).trim()) dish.category = categoryOf(row);
    const qty = olapNumber(row, FIELDS.qty);
    dish.names[name] = (dish.names[name] || 0) + qty;
    dish.qty += qty;
    dish.revenue += olapNumber(row, FIELDS.revenue);
    dish.cost += olapNumber(row, FIELDS.cost);
    dish.profit += olapNumber(row, FIELDS.profit);
    dishes.set(key, dish);
  }
  return [...dishes.values()].map(({ names, ...dish }) => ({
    ...dish,
    name: Object.entries(names).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ru'))[0][0],
    category: dish.category || NO_CATEGORY,
  }));
}

const share = (part, total) => (total > 0 ? (part / total) * 100 : 0);

function emptyResult(categories, excluded) {
  return {
    dishes: [],
    totals: { revenue: 0, cost: 0, profit: 0, qty: 0, dishes: 0 },
    groups: Object.fromEntries(LETTERS.map((l) => [l, { dishes: 0, profitShare: 0, revenueShare: 0 }])),
    matrix: LETTERS.map(() => LETTERS.map(() => 0)),
    tagCounts: Object.fromEntries(TAG_ORDER.map((t) => [t, 0])),
    excluded,
    categories,
  };
}

/**
 * rows — data ответа /menu_abc; selection — выбор из шапки («Все», «ресторан», «ресторан␟группа»);
 * category — «Все» или категория из categories.
 * → { dishes (по прибыли, по убыванию), totals, groups (A/B/C по прибыли: блюд, доля прибыли и выручки, %),
 *     matrix (строки — буква по прибыли, столбцы — по порциям), tagCounts, excluded (блюд не учтено),
 *     categories (категории выбора — для фильтра) }
 */
export function buildMenuAbc(rows, selection = ALL, category = ALL) {
  const selected = (Array.isArray(rows) ? rows : []).filter((row) => matchesSelection(row, selection));
  const categories = [...new Set(selected.map(categoryOf))].sort((a, b) =>
    (a === NO_CATEGORY) - (b === NO_CATEGORY) || a.localeCompare(b, 'ru'));
  const inCategory = category === ALL ? selected : selected.filter((row) => categoryOf(row) === category);

  const all = sumByDish(inCategory);
  const included = all.filter((d) => d.revenue > 0 && d.qty > 0);
  const excluded = all.length - included.length;
  if (included.length === 0) return emptyResult(categories, excluded);

  const byQty = lettersBy(included, 'qty');
  const byRevenue = lettersBy(included, 'revenue');
  const byProfit = lettersBy(included, 'profit');

  const dishes = included
    .map((dish) => {
      const result = {
        ...dish,
        profitPerPortion: dish.profit / dish.qty,
        abc: { qty: byQty.get(dish.key), revenue: byRevenue.get(dish.key), profit: byProfit.get(dish.key) },
        noCost: dish.revenue > 0 && dish.cost === 0,
      };
      return { ...result, tag: tagOf(result) };
    })
    .sort((a, b) => b.profit - a.profit || byName(a, b));

  // Разные блюда с одним названием (свои товары в разных ресторанах сети) — в списке подпись с категорией
  const nameCounts = {};
  dishes.forEach((d) => { nameCounts[d.name] = (nameCounts[d.name] || 0) + 1; });
  dishes.forEach((d) => { d.duplicateName = nameCounts[d.name] > 1; });

  const totals = dishes.reduce(
    (sum, d) => ({ revenue: sum.revenue + d.revenue, cost: sum.cost + d.cost, profit: sum.profit + d.profit, qty: sum.qty + d.qty }),
    { revenue: 0, cost: 0, profit: 0, qty: 0 },
  );
  totals.dishes = dishes.length;

  const groups = Object.fromEntries(LETTERS.map((letter) => {
    const inGroup = dishes.filter((d) => d.abc.profit === letter);
    return [letter, {
      dishes: inGroup.length,
      profitShare: share(inGroup.reduce((sum, d) => sum + d.profit, 0), totals.profit),
      revenueShare: share(inGroup.reduce((sum, d) => sum + d.revenue, 0), totals.revenue),
    }];
  }));

  const matrix = LETTERS.map(() => LETTERS.map(() => 0));
  const tagCounts = Object.fromEntries(TAG_ORDER.map((t) => [t, 0]));
  for (const dish of dishes) {
    matrix[LETTERS.indexOf(dish.abc.profit)][LETTERS.indexOf(dish.abc.qty)] += 1;
    tagCounts[dish.tag] += 1;
  }

  return { dishes, totals, groups, matrix, tagCounts, excluded, categories };
}
